import type { ReactNode } from 'react'
import { AlertCircle, Inbox, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { TableCell, TableRow } from '@/components/ui/table'
import { errorMessage } from '@/lib/api'

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
      <Inbox className="size-6 text-muted-foreground" aria-hidden />
      <p className="font-medium">{title}</p>
      {children && <p className="max-w-sm text-muted-foreground">{children}</p>}
      {action}
    </div>
  )
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center" role="alert">
      <AlertCircle className="size-6 text-risk" aria-hidden />
      <p className="font-medium">Couldn't load this</p>
      <p className="max-w-sm text-muted-foreground">{errorMessage(error)}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw /> Try again
        </Button>
      )}
    </div>
  )
}

/** Placeholder rows while a table loads, so the layout doesn't jump. */
export function SkeletonRows({ columns, rows = 8 }: { columns: number; rows?: number }) {
  return Array.from({ length: rows }, (_, r) => (
    <TableRow key={r} className="hover:bg-transparent">
      {Array.from({ length: columns }, (_, c) => (
        <TableCell key={c}>
          <Skeleton className="h-4" style={{ width: `${55 + ((r * 7 + c * 13) % 40)}%` }} />
        </TableCell>
      ))}
    </TableRow>
  ))
}

/** Shown when the API answered but couldn't reach Firebase, so names and sign-in dates are missing. */
export function Warnings({ warnings }: { warnings: string[] | undefined }) {
  if (!warnings?.length) return null
  const firebase = warnings.includes('firebase_unavailable')
  const others = warnings.filter((w) => w !== 'firebase_unavailable')
  return (
    <div className="mb-3 rounded-lg border border-warn/30 bg-warn/10 px-3 py-2 text-warn" role="status">
      {firebase && <p>Firebase did not answer, so emails, names and sign-in dates may be missing or out of date.</p>}
      {others.length > 0 && <p>Some sections could not load: {others.map((w) => w.replace(/_unavailable$/, '').replace(/_/g, ' ')).join(', ')}.</p>}
    </div>
  )
}
